import React, { useRef, useEffect } from 'react'
import isNode from 'is-node'
import { inject, observer } from 'mobx-react'
import { getContentfulSrcSet } from 'src/utils/image'

import Image from 'src/components/UI/Image/Image'
import Brand from 'src/stores/brand'

import styles from './BrandHero.module.scss'
import { ArrowRightIcon } from '../UI/Icon/Icon'
import LinkComponent from '../UI/LinkComponent/LinkComponent'

type Props = {
  highlight: any
  splash?: boolean
  brand?: Brand
  intersect?: boolean
  critical?: boolean
}

const intersectionOptions = {
  threshold: 0.05,
  rootMargin: '0px 0px 100%',
}

type WrapperProps = {
  link: any
  wrapperClass: any
  children: any
}

const Wrapper = ({ link, wrapperClass, children }: WrapperProps) =>
  link ? (
    <LinkComponent to={link} className={wrapperClass}>
      {children}
    </LinkComponent>
  ) : (
      <div className={wrapperClass}>{children}</div>
    )

const BrandHero = ({
  highlight,
  splash,
  brand,
  intersect,
  critical,
}: Props) => {
  if (!highlight) {
    return null
  }

  const heroRef = useRef(null)

  if (!isNode && intersect) {
    const observer = new IntersectionObserver((entries: any, observer: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting && brand) {
          brand.setHeaderBackground(true)
        } else {
          if (brand) {
            brand.setHeaderBackground(false)
          }
        }
      })
    }, intersectionOptions)

    useEffect(() => {
      if (heroRef && heroRef.current) {
        observer.observe(heroRef.current)
      }
      return () => {
        observer.unobserve(heroRef.current)
      }
    })
  }

  const { title, link, buttonText, image } = highlight.fields

  const wrapperClass = splash
    ? [styles.brandHero, styles.splash].join(' ')
    : styles.brandHero

  return (
    <Wrapper link={link} wrapperClass={wrapperClass}>
      <div ref={heroRef} className={styles.imageWrapper}>
        {image && (
          <Image
            className={styles.heroImage}
            srcSet={getContentfulSrcSet(image.fields.file.url)}
            alt={image.fields.title}
            critical={critical ? true : false}
            sizes="100vw"
          />
        )}
      </div>
      <div className={styles.heroContent}>
        <div className={styles.heroCta}>
          <h1 className={styles.heroTitle}>{title}</h1>
          {buttonText && link && (
            <button className={styles.heroButton}>
              {buttonText}{' '}
              <ArrowRightIcon color="white" width={13} height={9} />
            </button>
          )}
          {buttonText && !link && (
            <div className={styles.heroButton}>{buttonText}</div>
          )}
        </div>
      </div>
    </Wrapper>
  )
}

export default inject('brand')(observer(BrandHero))
